import React from 'react';

import css from './LegalPage.module.css';

/**
 * A tiny, dependency-free block renderer for the in-repo legal/About content. Each content module
 * exports an array of blocks; this turns them into styled elements. Inline **bold** and
 * [label](url) links are supported in paragraph and list text. This deliberately avoids a markdown
 * library — the content is authored in-repo as structured data, so the surface is small and
 * predictable. (http/https links open in a new tab; mailto: links render as plain anchors.)
 *
 * Block types:
 *   { type: 'lead', text }        - intro paragraph (larger)
 *   { type: 'h2', text }          - section heading
 *   { type: 'h3', text }          - sub-heading
 *   { type: 'p', text }           - paragraph
 *   { type: 'ul' | 'ol', items }  - list (items may contain **bold**)
 *   { type: 'note', text }        - emphasised callout paragraph
 */

// Split a string on the `**bold**` fence and wrap odd segments in <strong>.
const renderBold = (text, keyPrefix) => {
  const parts = String(text).split('**');
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={`${keyPrefix}b${i}`} className={css.strong}>
        {part}
      </strong>
    ) : (
      <React.Fragment key={`${keyPrefix}t${i}`}>{part}</React.Fragment>
    )
  );
};

// Inline renderer: [label](url) links plus **bold** (in the non-link segments). Links are the only
// markdown-ish addition; everything else is plain text with the bold fence.
const LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;
const renderInline = text => {
  const str = String(text);
  const nodes = [];
  let lastIndex = 0;
  let k = 0;
  let match;
  LINK_RE.lastIndex = 0;
  while ((match = LINK_RE.exec(str)) !== null) {
    const [full, label, url] = match;
    if (match.index > lastIndex) {
      nodes.push(...renderBold(str.slice(lastIndex, match.index), `p${k}`));
    }
    const isExternal = /^https?:\/\//i.test(url);
    nodes.push(
      <a
        key={`l${k}`}
        className={css.link}
        href={url}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {label}
      </a>
    );
    lastIndex = match.index + full.length;
    k += 1;
  }
  if (lastIndex < str.length) {
    nodes.push(...renderBold(str.slice(lastIndex), 'e'));
  }
  return nodes;
};

const renderBlock = (block, index) => {
  switch (block.type) {
    case 'lead':
      return (
        <p key={index} className={css.lead}>
          {renderInline(block.text)}
        </p>
      );
    case 'h2':
      return (
        <h2 key={index} className={css.h2}>
          {renderInline(block.text)}
        </h2>
      );
    case 'h3':
      return (
        <h3 key={index} className={css.h3}>
          {renderInline(block.text)}
        </h3>
      );
    case 'note':
      return (
        <p key={index} className={css.note}>
          {renderInline(block.text)}
        </p>
      );
    case 'contact':
      // Tight address block (name + email) — the lines belong together, so no paragraph spacing
      // between them. Renders as <address> for semantics.
      return (
        <address key={index} className={css.contact}>
          {block.lines.map((line, i) => (
            <span key={i} className={css.contactLine}>
              {renderInline(line)}
            </span>
          ))}
        </address>
      );
    case 'founders':
      return (
        <div key={index} className={css.founders}>
          <div className={css.foundersImages}>
            {block.images.map((img, i) => (
              <img key={i} className={css.founderImg} src={img.src} alt={img.alt || ''} />
            ))}
          </div>
          {block.name ? <p className={css.foundersName}>{block.name}</p> : null}
          {block.role ? <p className={css.foundersRole}>{block.role}</p> : null}
        </div>
      );
    case 'ul':
      return (
        <ul key={index} className={css.ul}>
          {block.items.map((item, i) => (
            <li key={i} className={css.li}>
              {renderInline(item)}
            </li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol key={index} className={css.ol}>
          {block.items.map((item, i) => (
            <li key={i} className={css.li}>
              {renderInline(item)}
            </li>
          ))}
        </ol>
      );
    case 'p':
    default:
      return (
        <p key={index} className={css.p}>
          {renderInline(block.text)}
        </p>
      );
  }
};

const LegalContent = ({ blocks = [] }) => <>{blocks.map(renderBlock)}</>;

export default LegalContent;
